/* eslint-disable max-lines */
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnDestroy, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { getDownloadURL, getStorage, ref, uploadBytes } from '@angular/fire/storage';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ApiKeysComponent, CalendarConnectionsComponent, TransferOwnershipComponent } from '@app/domains/organisations/ui';
import { AlgoliaService, AuthService, DashboardStore, OrganisationService } from '@app/shared/data';
import { Organisation, User, UserBase, UserRole, UserType } from '@app/shared/domain';
import { InviteUserComponent } from '@app/shared/ui';
import { GateFeatureDirective, HasRoleDirective } from '@app/shared/util';
import { environment } from '@env';
import { SearchResponse } from 'algoliasearch';
import { FirebaseStorage, StorageReference } from 'firebase/storage';
import { ConfirmationService, MenuItem, MessageService, SelectItem, ToastMessageOptions } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { Select, SelectChangeEvent, SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { debounceTime, distinctUntilChanged, firstValueFrom, Observable, Subject } from 'rxjs';

@Component({
  selector: 'app-organisation-settings',

  templateUrl: './organisation-settings.html',
  imports: [
    CommonModule,
    FormsModule,
    NgOptimizedImage,
    ReactiveFormsModule,
    RouterModule,
    ButtonModule,
    TableModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    FileUploadModule,
    GateFeatureDirective,
    HasRoleDirective,
    MenuModule,
    SkeletonModule,
    TagModule,
    TooltipModule,
    ApiKeysComponent,
    CalendarConnectionsComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganisationSettingsComponent implements OnDestroy {
  private readonly _organisationService = inject(OrganisationService);

  private readonly _algoliaService = inject(AlgoliaService);

  private readonly _dashboardStore = inject(DashboardStore);

  private readonly _messageService = inject(MessageService);

  private readonly _confirmationService = inject(ConfirmationService);

  private readonly _dialogService = inject(DialogService);

  private readonly _formBuilder = inject(FormBuilder);

  private readonly _functions = inject(Functions);

  private readonly _authService = inject(AuthService);

  private readonly _destroyRef = inject(DestroyRef);

  private readonly _router = inject(Router);

  private searchSubject = new Subject<string>();

  readonly fileUpload = viewChild<FileUpload>('fileUpload');

  readonly organisationAlias: string = environment.businessInfo.organisationAlias;

  readonly index = 'users';

  organisationForm: FormGroup;

  organisation$: Observable<Organisation>;

  avatarMenu: MenuItem[] = [
    {
      label: 'Organisation Icon',
      items: [
        {
          label: 'Upload Avatar',
          icon: 'pi pi-upload',
          command: (): void => {
            this.fileUpload().onBasicUploaderClick();
          },
        },
        {
          label: 'Restore Default',
          icon: 'pi pi-refresh',
          command: (): void => {
            this.clearAvatar();
          },
        },
      ],
    },
  ];

  readonly isEditMode = signal(false);

  readonly users = signal<User[]>([]);

  readonly totalRecords = signal(0);

  rows = 10;

  readonly loading = signal(false);

  readonly exporting = signal(false);

  readonly deletingOrganisation = signal(false);

  readonly searchQuery = signal('');

  updatingRoles = new Map<string, boolean>();

  roleOptions: SelectItem[] = [
    { label: UserType.Admin, value: UserRole.Admin },
    { label: UserType.Collaborator, value: UserRole.Collaborator },
  ];

  readonly UserRole = UserRole;

  constructor() {
    this.roleOptions = this.roleOptions.filter((role) => role.value >= this._dashboardStore.currentRole());
    // Initialize form
    this.organisationForm = this._formBuilder.group({
      name: new FormControl(),
      description: new FormControl(),
    });

    // Initialize organisation$
    this.organisation$ = this._organisationService.getByID(this._dashboardStore.selectedOrganisationId());

    // Update form reactively
    this.organisation$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((org) => {
      if (org) {
        this.organisationForm.patchValue({
          name: org.name,
          description: org.description,
        });
        this.organisationForm.disable();
        this.isEditMode.set(false);
      } else {
        this.organisationForm.reset();
      }
    });

    // Initialize search
    this.searchSubject.pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this._destroyRef)).subscribe((query) => {
      this.searchQuery.set(query);
      this.loadUsers({ first: 0, rows: this.rows });
    });
  }

  /**
   * Update the user profile after form submission
   * @param organisation organisation to update
   */
  async updateOrganisation(organisation: Organisation): Promise<void> {
    await firstValueFrom(
      this._organisationService.update(organisation.id, new Organisation({ ...organisation, ...this.organisationForm.value }).asDataObj),
    );
    this.toggleOrganisationEdit();

    this._messageService.add({
      summary: `${this.organisationAlias} Updated`,
      detail: `Your ${this.organisationAlias}  has been saved successfully.`,
      severity: 'success',
    });
  }

  /**
   * Toggles whether the profile information is being edited or not
   */
  toggleOrganisationEdit(): void {
    this.isEditMode.update((v) => !v);
    if (this.isEditMode()) {
      this.organisationForm.enable();
    } else {
      this.organisationForm.disable();
    }
  }

  /**
   * Set new image file when uploaded
   * @param $event image upload event
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async setImageFile($event: any, fileUploader: FileUpload, organisation: Organisation): Promise<void> {
    if ($event.files?.length > 0) {
      const file: File = $event.files[0];
      const storage: FirebaseStorage = getStorage();
      const storageRef: StorageReference = ref(storage, `/organisations/${organisation.id}/icon/${file.name}`);

      try {
        await uploadBytes(storageRef, file);
        const downloadUrl: string = await getDownloadURL(storageRef);

        await firstValueFrom(
          this._organisationService.update(organisation.id, new Organisation({ ...organisation, ...{ avatar: downloadUrl } }).asDataObj),
        );

        fileUploader.clear();
        this._messageService.add({
          summary: `${this.organisationAlias} Icon Updated`,
          detail: `${this.organisationAlias} icon successfully updated to ${file.name}.`,
          severity: 'success',
        });
      } catch {
        this._messageService.add({
          summary: 'Error Uploading Icon',
          detail: 'Please try again.',
          severity: 'error',
        });
      }
    } else {
      this._messageService.add({
        summary: 'No File Found',
        detail: 'No image file found.',
        severity: 'warn',
      });
    }
  }

  /**
   * Resets the URL for the organisation avatar
   */
  private async clearAvatar(): Promise<void> {
    const organisation: Organisation = await firstValueFrom(
      this._organisationService.getByID(this._dashboardStore.selectedOrganisationId()),
    );
    try {
      await firstValueFrom(
        this._organisationService.update(organisation.id, new Organisation({ ...organisation, ...{ avatar: '' } }).asDataObj),
      );
      this._messageService.add({
        summary: `${this.organisationAlias} Icon Reset`,
        detail: `${this.organisationAlias} icon successfully removed.`,
        severity: 'success',
      });
    } catch {
      this._messageService.add({
        summary: `Error Resetting ${this.organisationAlias} Icon`,
        detail: 'Please try again.',
        severity: 'error',
      });
    }
  }

  /**
   * Lazy load data based on pagination and filters
   */
  loadUsers(event: TableLazyLoadEvent): void {
    this.loading.set(true);

    const page = event.first && event.rows ? event.first / event.rows : 0;
    const rows = event.rows || this.rows;

    const searchParams = {
      hitsPerPage: rows,
      page: page,
      query: this.searchQuery(),
      facetFilters: [`organisationIds:${this._dashboardStore.selectedOrganisationId()}`],
    };

    // Use the centralized secured API key from AlgoliaService
    this._algoliaService
      .searchWithSecuredKey(this.index, searchParams)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response: SearchResponse) => {
          this.users.set(response.hits.map((hit) => new User(hit as UserBase)));
          this.totalRecords.set(response.nbHits);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this._messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load users',
          });
        },
      });
  }

  /**
   * Export users to CSV
   */
  exportToCsv(): void {
    this.exporting.set(true);
    const headers = ['Name', 'Email', 'Updated At', 'Last Logged In'];
    const rows = this.users().map((user) => [
      `"${user.name?.replace(/"/g, '""') || ''}"`,
      `"${user.email?.replace(/"/g, '""') || ''}"`,
      user.updatedAt || '',
      user.lastLoginTime || '',
    ]);

    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${this.index}_export.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    this.exporting.set(false);
  }

  /**
   * Get roles for display
   */
  getRoles(roles: Record<string, UserRole> | Map<string, UserRole>): string[] {
    const roleMap = roles instanceof Map ? roles : new Map(Object.entries(roles));
    return Array.from(roleMap.values()).map((role) => UserType[UserRole[role]]);
  }

  /**
   * Handle search query changes
   */
  async onSearchQueryChange(query: string): Promise<void> {
    this.searchSubject.next(query);
  }

  /**
   * Open invite user dialog
   */
  invite(organisation: Organisation): void {
    this._dialogService.open(InviteUserComponent, {
      header: `Invite to ${organisation.name}`,
      width: '40vw',
      breakpoints: { '1199px': '75vw', '575px': '90vw' },
      data: organisation,
      closable: false,
      modal: true,
      dismissableMask: false,
    });
  }

  async updateUserRole(
    dropdown: Select,
    org: Organisation,
    userId: string,
    event: SelectChangeEvent,
    originalRole: UserRole,
  ): Promise<void> {
    this.updatingRoles.set(userId, true);
    try {
      const setRole = httpsCallable(this._functions, 'setRole');
      const { data }: { data: ToastMessageOptions } = await setRole({
        organisationId: org.id,
        uid: userId,
        role: event.value,
      });

      this._messageService.add(data);

      if (data.severity === 'error') {
        dropdown.modelValue.set(originalRole);
      }
    } catch {
      dropdown.modelValue.set(originalRole);
      this._messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update role',
      });
    } finally {
      this.updatingRoles.set(userId, false);
    }
  }

  async sendPasswordReset(user: User): Promise<void> {
    this._confirmationService.confirm({
      message: `Are you sure you want to send a password reset email to ${user.email}?`,
      header: 'Send Password Reset',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: async () => {
        try {
          await this._authService.sendPasswordResetEmail(user.email, `${environment.appDomain}`);
          this._messageService.add({
            summary: 'Password Reset Email Sent',
            detail: `A password reset email has been sent to ${user.email}`,
            severity: 'success',
          });
        } catch {
          this._messageService.add({
            summary: 'Error',
            detail: 'Failed to send password reset email',
            severity: 'error',
          });
        }
      },
    });
  }

  confirmRemoveUser(user: User): void {
    this._confirmationService.confirm({
      message: `Are you sure you want to remove ${user.firstName} ${user.lastName} from the organisation?`,
      header: 'Remove User',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: async () => {
        try {
          const organisation = await firstValueFrom(this.organisation$);
          if (!organisation) {
            return;
          }

          const removeRole = httpsCallable(this._functions, 'removeRole');
          const { data }: { data: ToastMessageOptions } = await removeRole({
            organisationId: organisation.id,
            uid: user.id,
          });

          this._messageService.add(data);

          // Reload users to reflect changes
          this.loadUsers({ first: 0, rows: this.rows });
        } catch {
          this._messageService.add({
            summary: 'Error',
            detail: 'Failed to remove user from organisation',
            severity: 'error',
          });
        }
      },
    });
  }

  /**
   * Open transfer ownership dialog
   */
  transferOwnership(organisation: Organisation): void {
    this._dialogService.open(TransferOwnershipComponent, {
      header: `Transfer Ownership of ${organisation.name}`,
      width: '40vw',
      breakpoints: { '1199px': '75vw', '575px': '90vw' },
      data: organisation.id,
      closable: false,
      modal: true,
      dismissableMask: false,
    });
  }

  /**
   * Confirm deletion of organisation with warning dialog
   */
  confirmDeleteOrganisation(organisation: Organisation): void {
    this._confirmationService.confirm({
      message:
        `Are you sure you want to delete "${organisation.name}"?` +
        ` This action is irreversible and will delete all associated organisation data.` +
        ` All members will no longer be allowed access to this organisation.`,
      header: 'Delete Organisation',
      rejectButtonStyleClass: 'p-button-outlined',
      acceptButtonStyleClass: 'p-button-danger',
      accept: async () => {
        this.deletingOrganisation.set(true);

        try {
          const deleteOrganisation = httpsCallable(this._functions, 'deleteOrganisation');
          const { data }: { data: ToastMessageOptions } = await deleteOrganisation({
            organisationId: organisation.id,
          });

          this._messageService.add(data);

          // If successful, navigate to dashboard
          if (data.severity === 'success') {
            // Clear the selected organisation from the store
            this._dashboardStore.clearOrganisationId();

            this._router.navigate(['/dashboard']);
          }
        } catch {
          this._messageService.add({
            summary: 'Error',
            detail: 'Failed to delete organisation',
            severity: 'error',
          });
        } finally {
          this.deletingOrganisation.set(false);
        }
      },
    });
  }

  ngOnDestroy(): void {
    // Release the index to decrement reference count and clean up if no longer needed
    this._algoliaService.releaseIndex(this.index);
  }
}
